/* ══════════════════════════════════════════════════════════════
   Index Cards — Index Price Cards with Sparklines  
   ══════════════════════════════════════════════════════════════ */

import { fetchIndices } from '../services/market-data';
import { renderUnavailableBlock } from './data-state';

export interface IndexCardData {
  name: string;
  value: number;
  change: number;
  changePct: number;
  sparkline: number[];
  high52w: number;
  low52w: number;
}

function miniSparkline(values: number[], color: string): string {
  if (values.length < 2) return '';
  const min = Math.min(...values), max = Math.max(...values), range = max - min || 1;
  const w = 100, h = 30, step = w / (values.length - 1);
  const pts = values.map((v, i) => `${i * step},${h - ((v - min) / range * h)}`).join(' ');
  return `<svg width="${w}" height="${h}" class="spark"><polyline points="${pts}" fill="none" stroke="${color}" stroke-width="1.5"/></svg>`;
}

function renderWithData(container: HTMLElement, data: IndexCardData[]): void {
  const cards = data.map(idx => {
    const isUp = idx.change >= 0;
    const color = isUp ? 'var(--india-green)' : 'var(--india-red)';
    const arrow = isUp ? '▲' : '▼';
    return `
      <div class="index-card glass-card">
        <div class="index-header">
          <span class="index-name">${idx.name}</span>
          ${miniSparkline(idx.sparkline, color)}
        </div>
        <div class="index-value">${idx.value.toLocaleString('en-IN')}</div>
        <div class="index-change" style="color:${color}">
          ${arrow} ${Math.abs(idx.change).toLocaleString('en-IN')} (${idx.changePct >= 0 ? '+' : ''}${idx.changePct.toFixed(2)}%)
        </div>
        <div class="index-range">
          <small>52w: ${idx.low52w.toLocaleString('en-IN')} – ${idx.high52w.toLocaleString('en-IN')}</small>
        </div>
      </div>`;
  }).join('');

  container.innerHTML = `<div class="index-cards-grid">${cards}</div>`;
}

export function renderIndexCards(container: HTMLElement, indices?: IndexCardData[]): void {
  if (indices) {
    renderWithData(container, indices);
  } else {
    container.innerHTML = renderUnavailableBlock('index-cards-grid', 'Loading indices...');
    fetchIndices().then(apiIndices => {
      if (apiIndices && apiIndices.length > 0) {
        const mapped: IndexCardData[] = apiIndices.slice(0, 8).map(idx => ({
          name: idx.name || idx.symbol || '',
          value: idx.last || 0,
          change: idx.change || 0,
          changePct: idx.pChange || 0,
          sparkline: [idx.low, idx.open, idx.last].filter(v => v > 0), // minimal sparkline from OHLC
          high52w: 0, // NSE allIndices may not return 52w — use 0
          low52w: 0,
        }));
        renderWithData(container, mapped);
      } else {
        container.innerHTML = renderUnavailableBlock('index-cards-grid', 'Live indices unavailable');
      }
    }).catch(() => {
      container.innerHTML = renderUnavailableBlock('index-cards-grid', 'Live indices unavailable');
    });
  }
}
